import { TimeService, getMomentTz } from "@dexteel/mesf-core";
import { Clear } from "@mui/icons-material";
import { IconButton, InputAdornment } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";
import { FieldError } from "react-hook-form";

interface DateTimeDE {
	value: Date | null;
	format?: string;
	label?: string;
	disabled?: boolean;
	disableOpenPicker?: boolean;
	clearable?: boolean;
	onChange?: (value: Date | null) => void;
	onBlur?: (value: Date | null) => void;
	onFocus?: () => void;
	fieldError?: FieldError | undefined;
	skipBlurOnClear?: boolean;
	readOnly?: boolean;
}

const moment = getMomentTz();

export const DateTimePickerDE = ({
	value,
	format,
	label,
	disabled = false,
	disableOpenPicker = false,
	clearable = true,
	onChange = () => {},
	onBlur = () => {},
	onFocus = () => {},
	fieldError,
	skipBlurOnClear = false,
	readOnly = false,
}: DateTimeDE) => {
	const additionalProps: { [key: string]: any } = {};
	if (format) additionalProps["format"] = format;

	const handleClear = () => {
		onChange(null);
		if (!skipBlurOnClear) {
			setTimeout(() => {
				onBlur(null);
			}, 0);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			onBlur(value);
		}
	};

	return (
		<DateTimePicker
			ampm={false}
			disableFuture
			disableOpenPicker={disableOpenPicker}
			disabled={disabled}
			timezone={TimeService.getInstance().getServerTimeZone()}
			label={label}
			value={moment(value)}
			onChange={(value: Moment | null) => {
				if (value && value.isValid()) {
					onChange(value?.toDate());
				} else {
					onChange(null);
				}
			}}
			slotProps={{
				textField: {
					fullWidth: true,
					variant: "outlined",
					error: !!fieldError,
					helperText: fieldError?.message,
					onFocus,
					onKeyDown: handleKeyDown,
					onBlur: () => {
						if (value) {
							onBlur(value);
						} else {
							onBlur(null);
						}
					},
					InputProps: {
						endAdornment: (
							<>
								{clearable && (
									<InputAdornment position="end">
										<IconButton
											onClick={handleClear}
											disabled={!value || disabled}
											aria-label="clear date"
											edge="end"
										>
											<Clear />
										</IconButton>
									</InputAdornment>
								)}
							</>
						),
					},
					inputProps: {
						readOnly: readOnly,
					},
				},
			}}
			{...additionalProps}
		/>
	);
};
